import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Select, Spin } from 'antd';

export default function AsyncEntitySelect({
  value,
  onChange,
  onSelectItem,
  fetchOptions,
  minSearchLength = 1,
  placeholder = 'Search…',
  searchHint,
  disabled = false,
  allowClear = true,
}) {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const debounceRef = useRef(null);
  const fetchRef = useRef(fetchOptions);

  useEffect(() => {
    fetchRef.current = fetchOptions;
  }, [fetchOptions]);

  const runSearch = useCallback(
    async (query) => {
      const trimmed = query.trim();
      if (trimmed.length < minSearchLength) {
        setOptions([]);
        return;
      }
      setLoading(true);
      try {
        const data = await fetchRef.current(trimmed);
        const items = Array.isArray(data?.items) ? data.items : Array.isArray(data) ? data : [];
        setOptions(
          items.map((item) => ({
            value: item.uuid ?? item.id,
            label: item.label ?? item.name ?? String(item.id),
            item,
          })),
        );
      } catch {
        setOptions([]);
      } finally {
        setLoading(false);
      }
    },
    [minSearchLength],
  );

  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => runSearch(search), 350);
    return () => clearTimeout(debounceRef.current);
  }, [search, runSearch]);

  const handleChange = (nextValue, option) => {
    onChange?.(nextValue);
    onSelectItem?.(option?.item ?? null);
  };

  const notFoundContent = loading ? (
    <Spin size="small" />
  ) : search.trim().length < minSearchLength ? (
    searchHint || `Type at least ${minSearchLength} character${minSearchLength === 1 ? '' : 's'} to search`
  ) : (
    'No results found'
  );

  return (
    <Select
      showSearch
      allowClear={allowClear}
      value={value}
      onChange={handleChange}
      placeholder={placeholder}
      filterOption={false}
      onSearch={setSearch}
      loading={loading}
      disabled={disabled}
      options={options}
      notFoundContent={notFoundContent}
      className="w-full"
      style={{ width: '100%' }}
      optionFilterProp="label"
    />
  );
}
